import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase configuration. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY (see .env.example).'
  );
}

// 🚨 REQUEST MONITORING SYSTEM - Prevents excessive API calls
let requestCount = 0;
let requestWindowStart = Date.now();
const REQUEST_WINDOW = 60000; // 1 minute window
const MAX_REQUESTS_PER_WINDOW = 50; // Max 50 requests per minute
const requestLog: Array<{ timestamp: number; url: string; method: string }> = [];

// Reset counter every minute
setInterval(() => {
  const now = Date.now();
  if (now - requestWindowStart >= REQUEST_WINDOW) {
    console.log(`[Supabase Monitor] 📊 Request window reset. Previous window: ${requestCount} requests`);
    requestCount = 0;
    requestWindowStart = now;
    // Keep only recent requests in log
    const cutoff = now - REQUEST_WINDOW;
    requestLog.splice(0, requestLog.findIndex(req => req.timestamp > cutoff));
  }
}, 10000); // Check every 10 seconds

// Create Supabase client with request monitoring
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce'
  },
  global: {
    fetch: (url, options = {}) => {
      const now = Date.now();
      const method = options.method || 'GET';
      
      // Reset window if needed
      if (now - requestWindowStart >= REQUEST_WINDOW) {
        requestCount = 0;
        requestWindowStart = now;
      }
      
      requestCount++;
      requestLog.push({ timestamp: now, url: url.toString(), method });

      const urlObj = new URL(url.toString());
      const endpoint = urlObj.pathname;
      if (import.meta.env.DEV) {
        console.log(`[Supabase] 📡 Request #${requestCount}: ${method} ${endpoint}`);
      }
      
      // Rate limiting check
      if (requestCount > MAX_REQUESTS_PER_WINDOW) {
        console.error(`🚨 RATE LIMIT EXCEEDED! ${requestCount}/${MAX_REQUESTS_PER_WINDOW} requests in current window`);
        console.log('Recent requests:', requestLog.slice(-10));
        
        // Don't block the request, but log the issue
        console.warn('⚠️ Allowing request to proceed but this indicates a problem');
      }
      
      // Special monitoring for profile-related requests
      if (endpoint.includes('/profiles')) {
        const recentProfileRequests = requestLog.filter(
          req => req.timestamp > now - 30000 && req.url.includes('/profiles')
        ).length;
        
        if (recentProfileRequests > 10) {
          console.warn(`🔥 HIGH PROFILE REQUEST FREQUENCY: ${recentProfileRequests} profile requests in last 30s`);
        }
      }
      
      return fetch(url, options);
    }
  }
});

export async function getUserProfile(userId: string) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  if (error) throw error;
  return data;
}

export async function updateUserPreferredLanguage(userId: string, lang: string) {
  const { error } = await supabase
    .from('profiles')
    .update({ preferred_language: lang })
    .eq('id', userId);
  if (error) throw error;
}

export async function addUserProfile(userId: string, lang: string) {
  const { error } = await supabase
    .from('profiles')
    .insert([{ id: userId, preferred_language: lang }]);
  if (error) throw error;
}

export async function getAllTenants() {
  const { data, error } = await supabase.from('tenants').select('id, name');
  if (error) throw error;
  return data;
}

export async function uploadServiceOrderPhoto(file: File): Promise<string> {
  const fileName = `${Date.now()}_${file.name}`;
  const { error } = await supabase.storage.from('service-order-photos').upload(fileName, file);
  if (error) throw error;
  const { publicUrl } = supabase.storage.from('service-order-photos').getPublicUrl(fileName).data;
  return publicUrl;
}

export async function uploadBrandingLogo(file: File): Promise<string> {
  const fileName = `${Date.now()}_${file.name}`;
  const { error } = await supabase.storage.from('brandinglogos').upload(fileName, file);
  if (error) throw error;
  const { publicUrl } = supabase.storage.from('brandinglogos').getPublicUrl(fileName).data;
  return publicUrl;
}

export const getUsersForTenant = async (tenantId: string) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, first_name, last_name, email')
    .eq('tenant_id', tenantId);
  if (error) throw error;
  return data;
}; 